import { useState, useEffect } from 'react';
import { validateCurrentPage, SEOValidationResult } from '@/lib/seoValidator';
import { useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Bug } from 'lucide-react';

/**
 * SEO Debug Panel - Shows SEO validation status in development mode
 * Only renders in development environment
 */
const SEODebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<SEOValidationResult | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Only run in development
    if (!import.meta.env.DEV) return;

    // Wait for React Helmet to update the DOM
    const timer = setTimeout(() => {
      setResult(validateCurrentPage());
    }, 500);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Only render in development
  if (!import.meta.env.DEV) return null;

  if (!result) return null;

  const criticalIssues = result.issues.filter(i => i.startsWith('❌')).length;
  const warnings = result.issues.filter(i => i.startsWith('⚠️')).length;

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-4 z-[9999] p-3 rounded-full shadow-lg transition-all ${
          criticalIssues > 0 
            ? 'bg-red-500 hover:bg-red-600' 
            : warnings > 0 
              ? 'bg-yellow-500 hover:bg-yellow-600' 
              : 'bg-green-500 hover:bg-green-600'
        }`}
        title="SEO Debug Panel"
      >
        <Bug className="h-5 w-5 text-white" />
        {criticalIssues > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-red-500 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {criticalIssues}
          </span>
        )}
      </button>

      {/* Debug panel */}
      {isOpen && (
        <div className="fixed bottom-36 right-4 z-[9999] w-80 bg-background border border-border rounded-lg shadow-xl overflow-hidden">
          <div className={`px-4 py-2 flex items-center justify-between ${
            result.passed ? 'bg-green-500' : 'bg-red-500'
          }`}>
            <span className="text-white font-semibold flex items-center gap-2">
              {result.passed ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              SEO Validation
            </span>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-white/80"
            >
              ✕
            </button>
          </div>
          
          <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
            <div className="text-sm">
              <span className="text-muted-foreground">Page:</span>{' '}
              <span className="font-medium">{result.pageName}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className={`p-2 rounded ${result.metaDescriptions === 1 ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
                <div className="text-muted-foreground text-xs">Meta Desc</div>
                <div className="font-bold">{result.metaDescriptions}</div>
              </div>
              <div className={`p-2 rounded ${result.h1Tags >= 1 ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
                <div className="text-muted-foreground text-xs">H1 Tags</div>
                <div className="font-bold">{result.h1Tags}</div>
              </div>
            </div>

            {result.issues.length > 0 ? (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-muted-foreground uppercase">Issues</div>
                {result.issues.map((issue, i) => (
                  <div 
                    key={i} 
                    className={`text-sm p-2 rounded flex items-start gap-2 ${
                      issue.startsWith('❌') 
                        ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' 
                        : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                    }`}
                  >
                    {issue.startsWith('❌') ? (
                      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    )}
                    <span>{issue.replace(/^[❌⚠️]\s*/, '')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2 p-2 bg-green-100 dark:bg-green-900/20 rounded">
                <CheckCircle className="h-4 w-4" />
                All SEO checks passed!
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SEODebugPanel;
